import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	GlobalShortcutsMenu,
	type GlobalShortcutsItem,
	type GlobalShortcutsMenuProps,
	type ViewAllAppsConfig,
} from '@atlassian/global-shortcuts';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	GroupDropIndicatorWithDragOver,
	isL1ItemData,
	useDragState,
} from '@atlassian/navigation-drag-and-drop';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useInViewport } from '../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useItemsAndShortcuts } from '../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../controllers/options-provider';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';
import { useGlobalShortcutsTransformation } from '../../controllers/transformation/use-global-shortcuts-transformation';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import MoreMenu from '../more-menu';
import { INTERNAL_GLOBAL_ITEMS } from '../more-menu/constants';
import { HideFromSidebarButton } from '../more-menu/hide-from-sidebar-button';
import { ShowInSidebarButton } from '../more-menu/show-in-sidebar-button';
import { messages } from '../side-nav/messages';

export function PostMainItems({
	postItemsElem,
	postMainElem,
	draggingL1Group = null,
	openShortcutsInSameTab,
	viewAllAppsConfig,
	ShortcutsSlot,
}: PostMainItemsProps): React.JSX.Element {
	const { appLabel, disableMenuCustomization, intlMode, l1DragAndDropEnabled } = useOptions();
	const { formatMessage } = useIntl();

	const { allItems, growthItemShortcuts, shortcuts } = useItemsAndShortcuts();
	const peekMenuId = usePeekMenuId();

	const { hiddenItemMenuIds, hiddenShortcutMenuIds, visibleShortcutMenuIds, hiddenMenuIds } =
		useUserMenuDataShapes();

	const { fireClickedSideNavItemEvent } = useNavAnalytics();

	const containerRef = useRef<HTMLDivElement | null>(null);
	// Remove when cleaning up navx-4866-migrate-messages-from-nav-error-boundary
	const { isInViewport } = useInViewport({ ref: containerRef });

	const { shortcutsWithMenuId } = useGlobalShortcutsTransformation({
		shortcutsConfig: shortcuts,
		visibleShortcuts: visibleShortcutMenuIds,
		growthShortcutsConfig: growthItemShortcuts,
	});

	const { groupDropIndicatorRef, dragSource } = useDragState({
		dragAndDropEnabled: l1DragAndDropEnabled,
		isItemData: isL1ItemData,
	});

	const handleClickMoreMenuItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.more, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const handleClickCustomizeItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.customize, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const peekItem = !!peekMenuId && hiddenItemMenuIds.includes(peekMenuId) && allItems[peekMenuId];
	const isDragSourceHidden = dragSource && hiddenMenuIds.includes(dragSource);

	const canMenuItemBeHidden = useMemo(() => {
		return !!(draggingL1Group && dragSource && allItems[dragSource]?.isHideable !== false);
	}, [allItems, dragSource, draggingL1Group]);

	const moreMenu = (
		<MoreMenu
			appLabel={appLabel}
			intlMode={intlMode}
			items={allItems}
			itemIds={hiddenItemMenuIds}
			onClickCustomizeSidebarMenuItem={handleClickCustomizeItem}
			onClickMoreMenuTrigger={handleClickMoreMenuItem}
			openShortcutsInSameTab={openShortcutsInSameTab}
			shortcutItemIds={hiddenShortcutMenuIds}
			shortcutItems={shortcuts}
		/>
	);

	// Inline the gate (rather than calling `componentWithFG` inside the render
	// body) so the rendered tree's component identity is stable across renders.
	// Otherwise React would unmount/remount the entire shortcuts subtree on
	// every parent render, which tears down `useL1ItemDragAndDrop`'s internal
	// `state: 'dragging'` between PDND's `onDragStart` and the next render —
	// causing shortcut items to no longer visually "grey out" while dragged.
	// Remove the gate branch entirely when cleaning up
	// navx-4866-migrate-messages-from-nav-error-boundary.
	const globalAppShortcuts = fg('navx-4866-migrate-messages-from-nav-error-boundary') ? (
		<GlobalAppShortcuts
			shortcutItems={shortcutsWithMenuId}
			openShortcutsInSameTab={openShortcutsInSameTab}
			viewAllAppsConfig={viewAllAppsConfig}
			ShortcutsSlot={ShortcutsSlot}
		/>
	) : (
		<Box ref={containerRef}>
			<GlobalAppShortcuts
				shortcutItems={shortcutsWithMenuId}
				isInViewport={isInViewport}
				openShortcutsInSameTab={openShortcutsInSameTab}
				viewAllAppsConfig={viewAllAppsConfig}
				ShortcutsSlot={ShortcutsSlot}
			/>
		</Box>
	);

	return (
		<Stack space="space.100">
			{postItemsElem}
			{!!shortcutsWithMenuId.length &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={draggingL1Group === L1_MENU_GROUPS.SHORTCUTS}
						ariaLabel={formatMessage(messages.shortcutsSectionScreenReader, { item: dragSource })}
					>
						{globalAppShortcuts}
					</GroupDropIndicatorWithDragOver>
				) : (
					globalAppShortcuts
				))}
			{peekItem && (
				<MenuList testId={PEEK_TEST_ID}>
					<NavItem key={peekMenuId} id={peekMenuId} item={peekItem} isInMoreMenu peek />
				</MenuList>
			)}
			{!disableMenuCustomization &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={canMenuItemBeHidden}
						ariaLabel={
							isDragSourceHidden
								? formatMessage(messages.showInSidebarSectionScreenReader, { item: dragSource })
								: formatMessage(messages.hideFromSidebarSectionScreenReader, { item: dragSource })
						}
					>
						<Box xcss={cx(canMenuItemBeHidden ? styles.hidden : styles.visible)}>{moreMenu}</Box>
						<Box xcss={cx(canMenuItemBeHidden ? styles.visible : styles.hidden)}>
							<MenuList>
								{isDragSourceHidden ? <ShowInSidebarButton /> : <HideFromSidebarButton />}
							</MenuList>
						</Box>
					</GroupDropIndicatorWithDragOver>
				) : (
					moreMenu
				))}
			{postMainElem}
		</Stack>
	);
}

export interface PostMainItemsProps extends Pick<
	GlobalSideNavigationProps,
	| 'postItemsElem'
	| 'postMainElem'
	| 'openShortcutsInSameTab'
	| 'ShortcutsSlot'
	| 'viewAllAppsConfig'
> {
	draggingL1Group?: string | null;
}

const styles = cssMap({
	visible: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

function GlobalAppShortcuts({
	isInViewport, // Remove when cleaning up navx-4866-migrate-messages-from-nav-error-boundary
	openShortcutsInSameTab,
	shortcutItems,
	viewAllAppsConfig,
	ShortcutsSlot,
}: {
	isInViewport?: boolean; // Remove when cleaning up navx-4866-migrate-messages-from-nav-error-boundary
	openShortcutsInSameTab?: boolean;
	shortcutItems: GlobalShortcutsItem[];
	ShortcutsSlot?: GlobalSideNavigationProps['ShortcutsSlot'];
	viewAllAppsConfig?: ViewAllAppsConfig;
}) {
	const { app, intlMode, l1DragAndDropEnabled } = useOptions();
	const { fireViewedShortcutsEvent } = useNavAnalytics();

	// Remove when cleaning up navx-4866-migrate-messages-from-nav-error-boundary
	useLayoutEffect(() => {
		if (fg('navx-4866-migrate-messages-from-nav-error-boundary')) {
			return;
		}

		if (isInViewport) {
			fireViewedShortcutsEvent(shortcutItems);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInViewport]);

	const containerRef = useRef<HTMLDivElement | null>(null);
	const fireAnalytics = useCallback(
		() => fireViewedShortcutsEvent(shortcutItems),
		[fireViewedShortcutsEvent, shortcutItems],
	);
	const onInViewport = fg('navx-4866-migrate-messages-from-nav-error-boundary')
		? fireAnalytics
		: undefined;

	useInViewport({ ref: containerRef, onInViewport });

	// A pre-bound shortcuts menu handed to the `ShortcutsSlot` render prop. The
	// host renders this wherever it wants; any props it passes override the
	// GSN-internal defaults. Memoised to keep a stable component identity so the
	// slot subtree is not remounted on every render (see the drag-and-drop
	// rationale in the sibling comment in `PostMainItems`).
	const ShortcutsMenuComponent = useCallback(
		function BoundGlobalShortcutsMenu(
			overrides: Partial<Pick<GlobalShortcutsMenuProps, 'shortcutItems' | 'testId'>>,
		) {
			return (
				<GlobalShortcutsMenu
					shortcutItems={shortcutItems}
					testId={SHORTCUTS_TEST_ID}
					{...overrides}
					enableDragAndDrop={l1DragAndDropEnabled}
					intlMode={intlMode}
					openInSameTab={openShortcutsInSameTab}
					product={app}
					viewAllAppsConfig={viewAllAppsConfig}
				/>
			);
		},
		[app, intlMode, l1DragAndDropEnabled, openShortcutsInSameTab, shortcutItems, viewAllAppsConfig],
	);

	// Inline the gate (rather than calling `componentWithFG` inside the render
	// body) so the rendered tree's component identity is stable across
	// renders. See sibling comment in `PostMainItems` for the rationale.
	// Remove the gate branch entirely when cleaning up
	// navx-4866-migrate-messages-from-nav-error-boundary.
	const shortcutsMenu =
		ShortcutsSlot && fg('navx-5236-add-shortcuts-slot-prop') ? (
			<ShortcutsSlot Component={ShortcutsMenuComponent} shortcutItems={shortcutItems} />
		) : (
			<ShortcutsMenuComponent />
		);

	return fg('navx-4866-migrate-messages-from-nav-error-boundary') ? (
		<Box ref={containerRef}>{shortcutsMenu}</Box>
	) : (
		shortcutsMenu
	);
}
